// src/components/ui/modal-shell.js
//
// ModalShell — the one modal frame (docs/components.md §6 backlog: 23
// hand-rolled `fixed inset-0` sites). Portal to document.body, backdrop
// bg-black/80, Escape closes, focus moves in and is trapped, the page does
// not scroll behind it, focus returns to the opener on close. z-[110] so it
// also sits above the call overlay (z-[100]).
//
//   <ModalShell open onClose={close} labelledBy="add-funds-title" size="md">…</ModalShell>
//
// Migrate a hand-rolled modal onto it when you are editing that file anyway.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const SIZES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl' };
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export default function ModalShell({ open, onClose, labelledBy, size = 'md', children, className = '' }) {
  const panel = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = panel.current?.querySelector(FOCUSABLE);
    (first || panel.current)?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current?.(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = [...panel.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) { e.preventDefault(); return; }
      const a = items[0], z = items[items.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/80 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCloseRef.current?.(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1}
        className={`custom-scrollbar max-h-[92dvh] w-full ${SIZES[size] ?? SIZES.md} overflow-y-auto rounded-t-[24px] border border-white/[0.08] bg-[#141414] text-white shadow-[0_32px_100px_rgba(0,0,0,0.38)] outline-none sm:rounded-[24px] ${className}`}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
